import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

function GDPR() {
  const navigate = useNavigate();

  // SEO structured data
  const seoStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'GDPR Compliance',
    'description': 'GDPR compliance information and data protection rights for Pharmaceutical Database'
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1000px', margin: '0 auto' }}>
      <SEO
        title="GDPR Compliance - Pharmaceutical Database"
        description="Learn about GDPR compliance at Pharmaceutical Database. Understand your data protection rights, how we process personal data, and your rights under European data protection law."
        keywords="GDPR, data protection, GDPR compliance, data rights, EU privacy, personal data, data controller"
        canonical="/gdpr"
        structuredData={seoStructuredData}
      />
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: '20px',
          padding: '8px 16px',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          color: '#495057'
        }}
      >
        ← Back
      </button>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '40px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef'
      }}>
        <h1 style={{
          fontSize: '36px',
          marginBottom: '10px',
          color: '#212529',
          fontWeight: '700'
        }}>
          GDPR Compliance & Privacy Policy
        </h1>
        
        <p style={{
          fontSize: '14px',
          color: '#6c757d',
          marginBottom: '30px'
        }}>
          Last Updated: November 20, 2025
        </p>

        {/* Introduction */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            1. Introduction
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            This Pharmaceutical Database Application ("we", "our", or "the App") is committed to protecting your privacy and ensuring compliance with the General Data Protection Regulation (GDPR). This policy explains how we collect, use, and protect your personal data.
          </p>
        </section>

        {/* Data Controller */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            2. Data Controller
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            The data controller responsible for your personal data is the Pharmaceutical Database Application. For any privacy-related inquiries, please contact us through the Contact page.
          </p>
        </section>

        {/* Data We Collect */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            3. Data We Collect
          </h2>
          <div style={{ marginLeft: '20px' }}>
            <h3 style={{ fontSize: '18px', color: '#495057', marginBottom: '10px', fontWeight: '600' }}>
              3.1 Personal Data
            </h3>
            <ul style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
              <li>Account information (email address, username) if you register</li>
              <li>Search queries and browsing history within the application</li>
              <li>IP address and device information</li>
            </ul>

            <h3 style={{ fontSize: '18px', color: '#495057', marginBottom: '10px', fontWeight: '600' }}>
              3.2 Cookies and Tracking Technologies
            </h3>
            <ul style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
              <li><strong>Essential Cookies:</strong> Required for the app to function (authentication, session management)</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the app (with your consent)</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences</li>
            </ul>
          </div>
        </section>

        {/* Legal Basis */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            4. Legal Basis for Processing
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            We process your personal data under the following legal bases:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li><strong>Consent:</strong> You have given clear consent for us to process your data for specific purposes (e.g., analytics)</li>
            <li><strong>Contract:</strong> Processing is necessary for the performance of a contract with you</li>
            <li><strong>Legal Obligation:</strong> Processing is necessary to comply with legal requirements</li>
            <li><strong>Legitimate Interests:</strong> Processing is necessary for our legitimate interests (e.g., security, fraud prevention)</li>
          </ul>
        </section>

        {/* How We Use Your Data */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            5. How We Use Your Data
          </h2>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>To provide and maintain the pharmaceutical database service</li>
            <li>To personalize your experience and save your preferences</li>
            <li>To improve our services and develop new features</li>
            <li>To communicate with you about updates and changes</li>
            <li>To ensure security and prevent fraud</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        {/* Data Sharing */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            6. Data Sharing and Third Parties
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            We integrate with the following third-party pharmaceutical data sources:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>FDA Drugs@FDA Database</li>
            <li>FDA Orange Book</li>
            <li>ClinicalTrials.gov</li>
            <li>ChEBI (Chemical Entities of Biological Interest)</li>
            <li>ChEMBL Database</li>
            <li>PubChem</li>
            <li>RxNorm</li>
            <li>DailyMed</li>
          </ul>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            <strong>We do not sell your personal data to third parties.</strong> Data is only shared with third-party services necessary for the app's functionality and with your consent where required.
          </p>
        </section>

        {/* Your Rights */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            7. Your GDPR Rights
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            Under GDPR, you have the following rights:
          </p>
          <div style={{ marginLeft: '20px' }}>
            <div style={{
              backgroundColor: '#e7f3ff',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #007bff'
            }}>
              <strong style={{ color: '#004085' }}>Right to Access:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#004085' }}>
                Request copies of your personal data
              </p>
            </div>
            <div style={{
              backgroundColor: '#d4edda',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #28a745'
            }}>
              <strong style={{ color: '#155724' }}>Right to Rectification:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#155724' }}>
                Request correction of inaccurate or incomplete data
              </p>
            </div>
            <div style={{
              backgroundColor: '#fff3cd',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #ffc107'
            }}>
              <strong style={{ color: '#856404' }}>Right to Erasure:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#856404' }}>
                Request deletion of your personal data ("right to be forgotten")
              </p>
            </div>
            <div style={{
              backgroundColor: '#f8d7da',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #dc3545'
            }}>
              <strong style={{ color: '#721c24' }}>Right to Restrict Processing:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#721c24' }}>
                Request limitation of how we process your data
              </p>
            </div>
            <div style={{
              backgroundColor: '#d1ecf1',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #17a2b8'
            }}>
              <strong style={{ color: '#0c5460' }}>Right to Data Portability:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#0c5460' }}>
                Receive your data in a structured, machine-readable format
              </p>
            </div>
            <div style={{
              backgroundColor: '#e2e3e5',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #6c757d'
            }}>
              <strong style={{ color: '#383d41' }}>Right to Object:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#383d41' }}>
                Object to processing based on legitimate interests or direct marketing
              </p>
            </div>
            <div style={{
              backgroundColor: '#f3e5f5',
              padding: '15px',
              borderRadius: '6px',
              marginBottom: '10px',
              borderLeft: '4px solid #6f42c1'
            }}>
              <strong style={{ color: '#4a148c' }}>Right to Withdraw Consent:</strong>
              <p style={{ margin: '5px 0 0 0', color: '#4a148c' }}>
                Withdraw consent at any time (does not affect prior processing)
              </p>
            </div>
          </div>
          <p style={{ lineHeight: '1.8', color: '#212529', marginTop: '15px' }}>
            To exercise any of these rights, please contact us through the Contact page. We will respond within 30 days.
          </p>
        </section>

        {/* Data Retention */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            8. Data Retention
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            We retain your personal data only for as long as necessary to fulfill the purposes outlined in this policy:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li><strong>Account Data:</strong> Retained until you delete your account</li>
            <li><strong>Search History:</strong> Retained for 90 days or until cleared by you</li>
            <li><strong>Analytics Data:</strong> Anonymized after 12 months</li>
            <li><strong>Server Logs:</strong> Retained for 30 days for security purposes</li>
          </ul>
        </section>

        {/* Data Security */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            9. Data Security
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            We implement appropriate technical and organizational measures to protect your personal data:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of sensitive data at rest</li>
            <li>Regular security assessments and updates</li>
            <li>Access controls and authentication mechanisms</li>
            <li>Regular backups and disaster recovery procedures</li>
          </ul>
        </section>

        {/* International Transfers */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            10. International Data Transfers
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            Some of our third-party service providers may be located outside the European Economic Area (EEA). When transferring data internationally, we ensure adequate safeguards are in place, such as:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>Standard Contractual Clauses (SCCs) approved by the EU Commission</li>
            <li>Adequacy decisions by the EU Commission</li>
            <li>Other legally recognized transfer mechanisms</li>
          </ul>
        </section>

        {/* Children's Privacy */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            11. Children's Privacy
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            This application is intended for use by healthcare professionals, researchers, and adults. We do not knowingly collect personal data from children under 16 years of age. If we become aware of such collection, we will delete the data immediately.
          </p>
        </section>

        {/* Cookies Management */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            12. Managing Cookies
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            You can manage your cookie preferences through:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>The cookie consent banner when you first visit the app</li>
            <li>Your browser settings to block or delete cookies</li>
            <li>Opting out of analytics cookies while maintaining essential functionality</li>
          </ul>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            Note that blocking essential cookies may affect the functionality of the application.
          </p>
        </section>

        {/* Updates */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            13. Updates to This Policy
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            We may update this GDPR policy from time to time. We will notify you of significant changes by:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>Posting the new policy on this page with an updated revision date</li>
            <li>Sending an email notification if you have an account</li>
            <li>Displaying a prominent notice in the application</li>
          </ul>
        </section>

        {/* Complaints */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            14. Right to Lodge a Complaint
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            If you believe we have not handled your personal data properly, you have the right to lodge a complaint with your national data protection authority. You can find your local authority through the European Data Protection Board website.
          </p>
        </section>

        {/* Contact */}
        <section style={{ marginBottom: '30px' }}>
          <h2 style={{
            fontSize: '24px',
            color: '#495057',
            marginBottom: '15px',
            fontWeight: '600',
            borderBottom: '2px solid #007bff',
            paddingBottom: '8px'
          }}>
            15. Contact Us
          </h2>
          <p style={{ lineHeight: '1.8', color: '#212529', marginBottom: '15px' }}>
            For any questions about this GDPR policy or to exercise your rights, please:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#212529', marginLeft: '20px', marginBottom: '15px' }}>
            <li>Visit our <a href="/contact" style={{ color: '#007bff', textDecoration: 'none' }}>Contact page</a></li>
            <li>Email us with the subject line "GDPR Request"</li>
          </ul>
        </section>

        <div style={{
          marginTop: '40px',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          borderLeft: '4px solid #007bff'
        }}>
          <p style={{ margin: 0, color: '#495057', fontSize: '14px' }}>
            <strong>Effective Date:</strong> November 20, 2025<br />
            <strong>Last Reviewed:</strong> November 20, 2025
          </p>
        </div>
      </div>
    </div>
  );
}

export default GDPR;
